import { supabase } from './db.ts';

// Base URL for Supabase Edge Functions
const getFunctionUrl = (functionName: string): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/functions/v1/${functionName}`;
};

export type EdgeFunctionError = Error & { error_code?: string };

function throwEdgeFunctionHttpError(functionName: string, response: Response, errorData: Record<string, unknown>): never {
  const message =
    (typeof errorData.error === 'string' && errorData.error) ||
    (typeof errorData.message === 'string' && errorData.message) ||
    `Error calling ${functionName} (${response.status})`;
  const err = new Error(message) as EdgeFunctionError;
  if (typeof errorData.error_code === 'string') err.error_code = errorData.error_code;
  throw err;
}

// Helper to call any edge function with proper auth
async function callEdgeFunction<TPayload = Record<string, unknown>, TResponse = unknown>(
  functionName: string,
  payload: TPayload
): Promise<TResponse> {
  // Get the current session
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Not authenticated');
  }

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(getFunctionUrl(functionName), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...(anonKey ? { apikey: anonKey } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({} as Record<string, unknown>));
    throwEdgeFunctionHttpError(functionName, response, errorData);
  }

  return response.json();
}

// ==========================================
// Bet Functions
// ==========================================

/**
 * Place a pool-engine bet on a market outcome
 */
export async function placeBet(
  marketId: string,
  outcomeName: string,
  amount: number
): Promise<BetResponse> {
  return callEdgeFunction<
    { marketId: string; outcomeName: string; amount: number },
    BetResponse
  >('place-bet', {
    marketId,
    outcomeName,
    amount
  });
}

/**
 * Place a fixed-price (engine_type lmsr) bet via the place-bet Edge Function.
 * Normalizes `{ success, data: { ... } }` from the Edge function into a flat `LMSRBetResponse`.
 */
export async function placeLMSRBet(payload: LMSRBetPayload): Promise<LMSRBetResponse> {
  const raw = await callEdgeFunction<LMSRBetPayload, Record<string, unknown>>('place-bet', payload);
  if (raw && typeof raw === 'object' && 'data' in raw && raw.data && typeof raw.data === 'object') {
    const d = raw.data as Record<string, unknown>;
    const amount = Number(d.amount ?? 0);
    const shares = Number(
      d.shares_out ?? d.shares_owned ?? d.potential_win ?? 0,
    );
    const wb = d.wallet_balance;
    const newBalance = typeof wb === 'number' && !Number.isNaN(wb) ? wb : undefined;
    return {
      success: raw.success === true,
      betId: String(d.bet_id ?? ''),
      amount,
      amountAfterFee: Number(d.amount_after_fee ?? d.amount ?? 0),
      serviceFee: Number(d.service_fee ?? 0),
      shares_out: shares,
      lmsr_cost: Number(d.lmsr_cost ?? amount),
      price: Number(d.price_per_share ?? d.cost_per_share ?? 0),
      new_balance: newBalance,
      error: typeof raw.error === 'string' ? raw.error : undefined,
    };
  }
  return raw as LMSRBetResponse;
}

/** Payload from `sell_lmsr_shares` returned inside place-bet `data`. */
export interface LMSRSellResult {
  success?: boolean;
  gross_proceeds?: number;
  performance_fee?: number;
  net_proceeds?: number;
  cost_basis_sold?: number;
  error?: string;
  error_code?: string;
}

/** Sell LMSR shares before resolution (same Edge Function, direction=sell). */
export async function sellLMSRShares(payload: {
  option_id: number;
  shares: number;
  market_id: string;
  max_cost?: number;
}): Promise<LMSRSellResult> {
  const res = await callEdgeFunction<
    Record<string, unknown>,
    { success?: boolean; data?: LMSRSellResult; error?: string; error_code?: string }
  >('place-bet', {
    direction: 'sell',
    option_id: payload.option_id,
    market_id: payload.market_id,
    shares: payload.shares,
    max_cost: payload.max_cost,
  });
  if (res.success === false) {
    const err = new Error(res.error || 'Sell failed') as EdgeFunctionError;
    if (typeof res.error_code === 'string') err.error_code = res.error_code;
    throw err;
  }
  const inner = res.data;
  if (inner && inner.success === false) {
    const err = new Error(inner.error || 'Sell failed') as EdgeFunctionError;
    if (typeof inner.error_code === 'string') err.error_code = inner.error_code;
    throw err;
  }
  return inner ?? {};
}

export interface SubmitTradingMarketPayload {
  title: string;
  description: string;
  category: string;
  market_type: 'fixed' | 'knockout';
  is_private?: boolean;
  is_dynamic?: boolean;
  /** ISO datetime; omit or null when resolution is open-ended / TBA */
  resolution_time?: string | null;
  trading_end_time?: string | null;
  initial_options: {
    name: string;
    trading_end_time?: string | null;
    image_url?: string | null;
  }[];
  image_url: string;
  featured_image_url?: string | null;
  is_featured?: boolean;
}

export interface SubmitTradingMarketResponse {
  success: boolean;
  data?: {
    market_id: string;
    creator_deposit_amount?: number;
    lmsr_b?: number;
  };
  error?: string;
}

/**
 * Submit a share-priced market for approval (pending). An admin assigns liquidity tier
 * on approval; Wayja’s platform wallet funds LMSR collateral (see approve_pending_lmsr_market).
 */
export async function submitTradingMarket(
  payload: SubmitTradingMarketPayload,
): Promise<SubmitTradingMarketResponse['data']> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }
  const response = await fetch(getFunctionUrl('markets-submit'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });
  const json = (await response.json()) as SubmitTradingMarketResponse;
  if (!response.ok) {
    throw new Error(json.error || `markets-submit failed (${response.status})`);
  }
  if (!json.success) {
    throw new Error(json.error || 'Market submission failed');
  }
  return json.data;
}

/**
 * Cancel an active bet
 */
export async function cancelBet(betId: string): Promise<CancelBetResponse> {
  return callEdgeFunction<
    { betId: string },
    CancelBetResponse
  >('cancel-bet', {
    betId
  });
}

/**
 * Process a payout for a winning bet (admin only)
 */
export async function processPayout(
  betId: string,
  amount: number
): Promise<PayoutResponse> {
  return callEdgeFunction<
    { betId: string; amount: number },
    PayoutResponse
  >('payout', {
    betId,
    amount
  });
}

// ==========================================
// Wallet Functions
// ==========================================

interface DepositOptions {
  userId?: string;
  isAdminWallet?: boolean;
}

/**
 * Deposit funds into wallet
 */
export async function deposit(
  amount: number,
  options?: DepositOptions
): Promise<WalletResponse> {
  return callEdgeFunction<
    { amount: number } & Partial<DepositOptions>,
    WalletResponse
  >('deposit', {
    amount,
    ...(options || {})
  });
}

interface WithdrawOptions {
  userId?: string;
  isAdminWallet?: boolean;
}

/**
 * Withdraw funds from wallet
 */
export async function withdraw(
  amount: number,
  options?: WithdrawOptions
): Promise<WalletResponse> {
  return callEdgeFunction<
    { amount: number } & Partial<WithdrawOptions>,
    WalletResponse
  >('withdraw', {
    amount,
    ...(options || {})
  });
}

// ==========================================
// Peach Payment Functions
// ==========================================

interface CardData {
  number: string;
  holder: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

interface PeachPaymentOptions {
  paymentType: 'DB' | 'PA'; // Direct Debit or Pre-Authorization
  customerId?: string;
  merchantTransactionId?: string;
}

/**
 * Process a payment via Peach Payments
 */
export async function processPeachPayment(
  amount: number,
  cardData: CardData,
  returnUrl: string,
  options?: PeachPaymentOptions
): Promise<PeachPaymentResponse> {
  return callEdgeFunction<
    {
      amount: number;
      paymentType: 'DB' | 'PA';
      cardNumber: string;
      cardHolder: string;
      cardExpiryMonth: string;
      cardExpiryYear: string;
      cardCvv: string;
      returnUrl: string;
      customerId?: string;
      merchantTransactionId?: string;
    },
    PeachPaymentResponse
  >('peach_payment', {
    amount,
    paymentType: options?.paymentType || 'DB',
    cardNumber: cardData.number,
    cardHolder: cardData.holder,
    cardExpiryMonth: cardData.expiryMonth,
    cardExpiryYear: cardData.expiryYear,
    cardCvv: cardData.cvv,
    returnUrl,
    customerId: options?.customerId,
    merchantTransactionId: options?.merchantTransactionId
  });
}

/**
 * Check the status of a Peach Payment
 */
export async function checkPeachPaymentStatus(
  paymentId: string
): Promise<PeachPaymentStatusResponse> {
  return callEdgeFunction<
    { paymentId: string },
    PeachPaymentStatusResponse
  >('peach_payment_status', {
    paymentId
  });
}

/**
 * Create a Peach Checkout session
 */
export async function createPeachCheckout(
  amount: number,
  returnUrl: string,
  options?: {
    currency?: string;
    merchantTransactionId?: string;
    customerId?: string;
  }
): Promise<PeachCheckoutResponse> {
  return callEdgeFunction<
    {
      amountCents: number;
      currency?: string;
      returnUrl: string;
      merchantTransactionId?: string;
      customerId?: string;
    },
    PeachCheckoutResponse
  >('peach-initiate', {
    amountCents: Math.round(amount * 100), // Convert rands to cents
    currency: options?.currency,
    returnUrl,
    merchantTransactionId: options?.merchantTransactionId,
    customerId: options?.customerId
  });
}

// ==========================================
// Response Types
// ==========================================

export interface BetResponse {
  success: boolean;
  betId: string;
  amount: number;
  amountAfterFee: number;
  serviceFee: number;
  error?: string;
}

/** Payload for fixed-price bet placement via the place-bet Edge Function */
export interface LMSRBetPayload {
  option_id: number;
  amount?: number;
  market_id?: string;
  user_token?: string;
  direction?: 'buy' | 'sell';
  /** Required when direction is `sell` */
  shares?: number;
  /** Buy: max ZAR cost; sell: max gross proceeds (slippage guard) */
  max_cost?: number;
}

/** Response from a successful fixed-price bet */
export interface LMSRBetResponse extends BetResponse {
  shares_out?: number;   // shares the user received
  lmsr_cost?: number;    // actual ZAR charged (after fees)
  price?: number;        // price per share after the trade (0–1)
  /** Authoritative post-trade wallet balance from Edge (avoids header flicker) */
  new_balance?: number;
}

export interface MarketQuoteResponse {
  success: boolean;
  fair_probability?: number;
  cost_per_share?: number;
  shares_received?: number;
  potential_payout?: number;
  /** Always 0 at quote time; performance fee is not debited on buy. */
  est_performance_fee?: number;
  /** Legacy field: equals `potential_payout` (gross if you win), not a net-after-fee at buy. */
  est_net_to_user?: number;
  /** Only if the outcome wins: fee on (payout − stake), before resolution. */
  projected_performance_fee_if_win?: number;
  projected_net_payout_if_win?: number;
  spread_mode_applied?: string;
  spread_rate_applied?: number;
  quoted_at?: string;
  quote_valid_seconds?: number;
  error?: string;
}

export async function getMarketQuote(payload: {
  market_id: string;
  option_id: number;
  stake: number;
}): Promise<MarketQuoteResponse> {
  return callEdgeFunction<typeof payload, MarketQuoteResponse>('market-quote', payload);
}

export interface AdminFeesResponse {
  success: boolean;
  data: Record<string, unknown>;
}

export function getAdminFees(): Promise<AdminFeesResponse> {
  return callEdgeFunction<Record<string, unknown>, AdminFeesResponse>('admin-fees', { action: 'get' });
}

export function patchAdminFees(payload: Record<string, unknown>): Promise<AdminFeesResponse> {
  return callEdgeFunction<Record<string, unknown>, AdminFeesResponse>('admin-fees', payload);
}

export function patchAdminMarketFees(payload: Record<string, unknown>): Promise<AdminFeesResponse> {
  return callEdgeFunction<Record<string, unknown>, AdminFeesResponse>('admin-market-fees', payload);
}

export interface CancelBetResponse {
  success: boolean;
  betId: string;
  refundedAmount: number;
  refundedBetAmount: number;
  refundedServiceFee: number;
  error?: string;
}

export interface PayoutResponse {
  success: boolean;
  betId: string;
  userId: string;
  amount: number;
  status: string;
  error?: string;
}

export interface WalletResponse {
  success: boolean;
  transactionId: string;
  userId?: string;
  newBalance: number;
  amount: number;
  wallet?: string;
  error?: string;
}

export interface PeachPaymentResponse {
  success: boolean;
  paymentId: string;
  requiresRedirect: boolean;
  redirect?: {
    url: string;
    method: string;
    parameters?: Record<string, string>;
  };
  transactionId?: string;
  userId?: string;
  newBalance?: number;
  amount?: number;
  error?: string;
}

export interface PeachPaymentStatusResponse {
  success: boolean;
  paymentId: string;
  status: 'completed' | 'failed' | 'pending' | 'already_processed';
  transactionId?: string;
  userId?: string;
  newBalance?: number;
  amount?: number;
  message?: string;
  resultCode?: string;
  error?: string;
}

export interface PeachCheckoutResponse {
  success: boolean;
  checkoutId?: string;
  redirectUrl?: string;
  amount?: number;
  currency?: string;
  merchantTransactionId?: string;
  error?: string;
}

// ==========================================
// Hook Examples for React Components
// ==========================================

/*
Example of usage in a React component:

import { useState } from 'react';
import { placeBet } from '../lib/api';
import { toast } from 'react-hot-toast';

function PlaceBetForm() {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await placeBet('market-id', 'outcome-name', Number(amount));
      toast.success(`Trade placed! Amount: $${result.amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="Amount"
        required
      />
      <button type="submit" disabled={loading}>
        {loading ? 'Placing Trade...' : 'Place Trade'}
      </button>
    </form>
  );
}
*/ 